/**
 * ActivateAccount — Magic-Link-Aktivierung für SB-eingerichtete Mandanten.
 * Route: /aktivieren?token=...
 * Flow: Token validieren → Passwort setzen → direkter Login → Dashboard.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ShieldCheck, AlertTriangle, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = { fontFamily: 'Manrope, sans-serif' };

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Kein Aktivierungs-Token gefunden. Bitte nutze den Link aus deiner E-Mail.');
      setLoading(false);
      return;
    }
    axios.get(`${API}/auth/activation/${token}`)
      .then((res) => setInfo(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Aktivierungslink ungültig oder abgelaufen.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (password.length < 8) {
      setFormError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (password !== passwordConfirm) {
      setFormError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (!acceptTerms) {
      setFormError('Bitte akzeptiere die Nutzungsbedingungen und die Datenschutzerklärung.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/activation/${token}/complete`, { password, accept_terms: true });
      // Token kommt als httpOnly-Cookie — Full reload, AuthContext liest es via /auth/me.
      window.location.href = '/dashboard';
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Aktivierung fehlgeschlagen. Bitte versuche es erneut.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-bold tracking-tight text-slate-900" style={H}>ivoliq</Link>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm" data-testid="activate-account-card">
          {loading && (
            <div className="flex flex-col items-center py-10 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mb-3" />
              <p className="text-sm">Aktivierungslink wird geprüft…</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-6" data-testid="activation-error">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50">
                <AlertTriangle className="h-6 w-6 text-rose-500" />
              </div>
              <h1 className="text-lg font-semibold text-slate-900" style={H}>Link nicht gültig</h1>
              <p className="mt-2 text-sm text-slate-500">{error}</p>
              <Link to="/login">
                <Button variant="outline" className="mt-6" data-testid="activation-error-login-link">Zum Login</Button>
              </Link>
            </div>
          )}

          {!loading && info && (
            <>
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                  <ShieldCheck className="h-6 w-6 text-emerald-600" />
                </div>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={H}>
                  Willkommen{info.name ? `, ${info.name}` : ''}!
                </h1>
                <p className="mt-2 text-sm text-slate-500">
                  Dein Account <span className="font-medium text-slate-700">{info.email}</span> ist startklar.
                  Lege jetzt dein Passwort fest.
                </p>
              </div>

              {info.kanzlei_name && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3" data-testid="activation-kanzlei-hint">
                  <Building2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-emerald-900">
                    Eingerichtet durch <strong>{info.kanzlei_name}</strong> — deine Stammdaten und
                    Steuereinstellungen sind bereits hinterlegt. Du kannst alles jederzeit anpassen.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="activation-password" className="text-sm text-slate-700">Passwort</Label>
                  <Input
                    id="activation-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mindestens 8 Zeichen"
                    className="mt-1"
                    autoFocus
                    data-testid="activation-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="activation-password-confirm" className="text-sm text-slate-700">Passwort bestätigen</Label>
                  <Input
                    id="activation-password-confirm"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder="Passwort wiederholen"
                    className="mt-1"
                    data-testid="activation-password-confirm-input"
                  />
                </div>

                {/* Rechtliche Absicherung (Juni 2026): Nutzungsvertrag kommt erst mit Zustimmung zustande */}
                <label className="flex items-start gap-3 cursor-pointer" data-testid="activation-terms-label">
                  <Checkbox
                    checked={acceptTerms}
                    onCheckedChange={(v) => setAcceptTerms(!!v)}
                    className="mt-0.5"
                    data-testid="activation-terms-checkbox"
                  />
                  <span className="text-xs text-slate-600">
                    Ich akzeptiere die{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700" data-testid="activation-terms-link">
                      Nutzungsbedingungen
                    </a>{' '}
                    und die{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700" data-testid="activation-privacy-link">
                      Datenschutzerklärung
                    </a>{' '}
                    von ivoliq.
                  </span>
                </label>

                {formError && (
                  <p className="text-sm text-rose-600" data-testid="activation-form-error">{formError}</p>
                )}

                <Button
                  type="submit"
                  disabled={submitting || !password || !passwordConfirm || !acceptTerms}
                  className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="activation-submit-btn"
                >
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Account aktivieren
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Bereits aktiviert? <Link to="/login" className="text-emerald-600 hover:underline">Hier anmelden</Link>
        </p>
      </div>
    </div>
  );
}
